import type { ReactNode } from 'react';
import { readSession } from '../lib/api.js';
import { roleHasPermission } from '../lib/rbac-matrix.js';

type PermissionGateProps = {
  permission: string;
  children: ReactNode;
  /**
   * Rendered when the current staff role lacks the permission. Defaults to
   * `null` so call sites can wrap a button without leaking layout when the
   * user can't see it. Pass an explicit fallback (e.g. a disabled state or
   * a "request access" link) when silently hiding would confuse the user.
   */
  fallback?: ReactNode;
  /**
   * Override the role that's checked. When omitted (default) the gate reads
   * `staff.role` from the admin-app session in localStorage. The override
   * exists for tests and for places that already have a role in scope and
   * don't want a redundant localStorage read.
   */
  role?: string | null;
};

/**
 * Conditionally renders children based on the canonical RBAC matrix
 * (`lib/rbac-matrix.ts`).
 *
 * Three behaviors stack:
 *  - No session yet → renders fallback (treat as "no permissions").
 *  - `platform_admin` → always renders children (control-plane bypass).
 *  - Tenant role (owner/manager/etc.) → matches against the matrix.
 *
 * IMPORTANT: this is a UX gate, not a security boundary. The server's
 * `requirePermission()` middleware (RBAC-DESIGN-2026-04-21 §1.3) is the
 * authoritative check. PermissionGate exists so users don't see (and
 * mash) buttons that will 403 server-side. If the gate is wrong, the
 * server still rejects — never use it to relax server checks.
 */
export function PermissionGate({
  permission,
  children,
  fallback = null,
  role,
}: PermissionGateProps): JSX.Element {
  const effectiveRole = role !== undefined ? role : readSession().staff?.role ?? null;
  if (roleHasPermission(effectiveRole, permission)) {
    return <>{children}</>;
  }
  return <>{fallback}</>;
}
